import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { uploadManager, type UploadManagerState } from '@/lib/uploadManager';

/** The whole app-wide upload state. Re-renders on every progress tick. */
export function useUploadManagerState(): UploadManagerState {
  return useSyncExternalStore(uploadManager.subscribe, uploadManager.getSnapshot, uploadManager.getSnapshot);
}

/** Just "is anything uploading" — cheap enough for a page as large as the Library. */
export function useUploadsActive(): boolean {
  const get = () => uploadManager.getSnapshot().active;
  return useSyncExternalStore(uploadManager.subscribe, get, get);
}

/**
 * Seconds left for the bytes still to send, from a smoothed transfer rate.
 * Null until there is a rate worth quoting — a guess in the first second is
 * noise, and a wrong ETA reads worse than none.
 */
export function useUploadEta(loadedBytes: number, totalBytes: number, active: boolean): number | null {
  const last = useRef<{ t: number; b: number } | null>(null);
  const rate = useRef(0);
  const [eta, setEta] = useState<number | null>(null);
  useEffect(() => {
    if (!active) { last.current = null; rate.current = 0; setEta(null); return; }
    const now = Date.now();
    const prev = last.current;
    if (!prev || loadedBytes < prev.b) { last.current = { t: now, b: loadedBytes }; return; }
    const dt = (now - prev.t) / 1000;
    if (dt < 1) return;
    const inst = (loadedBytes - prev.b) / dt;
    rate.current = rate.current ? rate.current * 0.8 + inst * 0.2 : inst;
    last.current = { t: now, b: loadedBytes };
    const left = totalBytes - loadedBytes;
    setEta(rate.current > 1024 && left > 0 ? Math.round(left / rate.current) : null);
  }, [loadedBytes, totalBytes, active]);
  return eta;
}
